"use client";
import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { EmptyState } from '../../components/EmptyState';
import { Button } from '../../components/ui/button';
import { Archive } from 'lucide-react';

interface Memo {
    id: string;
    title: string;
    date: string;
    content: string;
    tags: string[];
}

export default function MemoArchivePage() {
    const [memos, setMemos] = useState<Memo[]>([]);
    const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null);

    useEffect(() => {
        // In a real implementation, this would fetch from Realm DB or API
        // For now, using placeholder data
        setMemos([]);
    }, []);

    return (
        <div className="space-y-6 max-w-6xl">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl md:text-3xl font-semibold">Memo Archive</h1>
                <Button onClick={() => setSelectedMemo(null)} variant="outline">
                    {selectedMemo ? 'Back to Archive' : 'Export All'}
                </Button>
            </div>

            {!selectedMemo ? (
                <div>
                    <p className="text-muted-foreground mb-4">
                        Your collection of reflections, insights, and notes from daily practices.
                    </p>
                    {memos.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                            {memos.map(memo => (
                                <Card
                                    key={memo.id}
                                    className="p-6 cursor-pointer hover:ring-1 ring-border transition-all overflow-hidden"
                                    onClick={() => setSelectedMemo(memo)}
                                >
                                    <div className="text-lg font-semibold mb-1 break-words">{memo.title}</div>
                                    <div className="text-sm text-muted-foreground mb-2">{memo.date}</div>
                                    <div className="text-sm line-clamp-2 break-words">{memo.content}</div>
                                    {memo.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {memo.tags.map(tag => (
                                                <span
                                                    key={tag}
                                                    className="text-xs px-2 py-1 bg-muted rounded-md"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            title="No memos yet"
                            message="Your reflections from daily rituals will appear here"
                            icon={Archive}
                        />
                    )}
                </div>
            ) : (
                <Card className="p-6 overflow-hidden w-full">
                    <div className="flex items-center justify-between mb-4 gap-4 w-full flex-wrap">
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-semibold break-words">{selectedMemo.title}</h2>
                            <div className="text-sm text-muted-foreground mt-1">{selectedMemo.date}</div>
                        </div>
                    </div>
                    <div className="prose prose-sm max-w-none break-words overflow-wrap-anywhere">{selectedMemo.content}</div>
                    {selectedMemo.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            {selectedMemo.tags.map(tag => (
                                <span key={tag} className="text-xs px-2 py-1 bg-muted rounded-md">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}

